import React, {useEffect, useState} from 'react';
import classnames from 'classnames';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import './styles.scss';
import Checkbox from '../Checkbox';

export interface TreeSelectNodeData {
    value: string,
    label: string,
    isCollapsed?: boolean,
    isSelected?: boolean,
    isCurrent?: boolean,
    isDisabled?: boolean,
    maxSelectableChildren?: number,
    parent?: TreeSelectNodeData,
    childNodes: TreeSelectNodeData[],
    isPreselected?: boolean,
}

export interface TreeSelectProps {
    data: TreeSelectNodeData[],
    onSelect?: any
}

export interface TreeSelectNodeProps {
    node: TreeSelectNodeData,
    data: TreeSelectNodeData[],
    dataValueMap: Map<string, TreeSelectNodeData>,
    draw: number,
    setDraw: any,
    setData: any,
    onSelect?: any
}

function TreeSelect(props: TreeSelectProps) {

    const dataValueMap: Map<string, TreeSelectNodeData> = new Map<string, any>();

    /**
     * Assign the parents to each node of the tree data. Recursively called.
     * 
     * @param node 
     * @param parent 
     */
    const assignParents = (node: TreeSelectNodeData, parent: TreeSelectNodeData) => {
        node.parent = parent;
        node.childNodes.forEach((child) => {
            assignParents(child, node);
        });
    };

    // Iniital call of assignParents.
    props.data.forEach((node) => {
        node.childNodes.forEach((child) => {
            assignParents(child, node);
        });
    });

    /**
     * Map out the nodes resursively by the node's value. The component expects the values to be unique.
     * 
     * @param node 
     */
    const mapChildren = (node: TreeSelectNodeData) => {
        node.childNodes.forEach((child) => {
            dataValueMap.set(child.value, child);
            mapChildren(child);
        });
    };

    // Build the dataValueMap
    props.data.forEach((node) => {
        dataValueMap.set(node.value, node);
        mapChildren(node);
    });

    const [data, setData] = useState(Object.assign(props.data, {}));
    const [draw, setDraw] = useState(0);

    return (
        <div className="tree-select">
            {props.data && props.data.map((node: TreeSelectNodeData, index) => {
                return <TreeSelectNode
                            data={data}
                            dataValueMap={dataValueMap}
                            draw={draw}
                            key={index}
                            setData={setData}
                            setDraw={setDraw}
                            node={node}
                            onSelect={props.onSelect}
                            ></TreeSelectNode>
            })}
        </div>
    );

}

function TreeSelectNode(props: TreeSelectNodeProps) {

    useEffect(() => {
        preselectNodes(props.node);
    }, []);

    /**
     * Triggers the selection mechanism for preselected nodes.
     */
    const preselectNodes = (node: TreeSelectNodeData) => {
        if (node.isPreselected) {
            node.isSelected = true;
            props.onSelect(node);
        }
        
        node.childNodes.forEach((child: TreeSelectNodeData) => {
            if (props.dataValueMap.has(child.value)) {
                child.isSelected = node.isSelected;
                child.isDisabled = node.isSelected;
            }
            preselectNodes(child);
        });
    }


    // Temporary object for calculating the initial state of hideChildren.
    const initialHideChildren: any = {};
    const initializeHiddenState = (node: TreeSelectNodeData) => {

        initialHideChildren[node.value] = node.isCollapsed;

        node.childNodes.forEach((child) => {
            initialHideChildren[child.value] = child.isCollapsed;
            initializeHiddenState(child);
        });

    };

    // Calculate the initial state of the nodes.
    initializeHiddenState(props.node);

    // State object to keep track of which nodes are collapsed/expanded.
    const [hideChildren, setHideChildren] = useState<any>(initialHideChildren);

    /**
     * Shows/hides a node's children.
     * 
     * @param node 
     */
    const toggleChildren = (node: TreeSelectNodeData) => {
        setHideChildren({ ...hideChildren, [node.value]: !hideChildren[node.value] });
    };

    /**
     * Checks or unchecks the given node's children.
     * 
     * @param node 
     */
    const checkUncheckChildren = (node: TreeSelectNodeData) => {
        node.childNodes.forEach((child: TreeSelectNodeData) => {
            // Only update the child's disabled property if it is not the currently selected value (can't be unselected).
            if (props.dataValueMap.has(child.value) && !props.dataValueMap.get(child.value)?.isCurrent) {
                child.isSelected = node.isSelected;
                child.isDisabled = node.isSelected;
            }
            checkUncheckChildren(child);
        });
    };

    /**
     * Renders a node and its children recursively.
     * 
     * @param node The parent node
     * @returns 
     */
    const renderNode = (node: TreeSelectNodeData) => {

        return (
            <div className={classnames('tree-select-node', { 'has-children': node.childNodes.length, 'root-node': !node.parent })}>
                <div className="node-info">
                    {node.parent && node.childNodes && node.childNodes.length > 0 && !hideChildren[node.value] &&
                        <KeyboardArrowDownIcon height="32" width="32" onClick={() => { toggleChildren(node); }}></KeyboardArrowDownIcon>}
                    {node.parent && node.childNodes && node.childNodes.length > 0 && hideChildren[node.value] && 
                        <KeyboardArrowRightIcon height="32" width="32" onClick={() => { toggleChildren(node); }}></KeyboardArrowRightIcon>}
                    <Checkbox
                        checked={node.isSelected}
                        disabled={node.isDisabled}
                        value={node.value}
                        label={node.label}
                        onClick={() => {

                            // console.debug('Node:', node);

                            let parent = node.parent;
                            const check = !node.isSelected;

                            // If there's no parent, it's the top node, so simply check/uncheck the node and then the children.
                            if (!parent) {
                                node.isSelected = check;
                                checkUncheckChildren(node);
                            }

                            // If there is a parent, we need to keep bubbling up to the top to see which checkboxes need to be checked.
                            while (parent != null) {

                                // Check the selected node.
                                node.isSelected = check;

                                // Get the number of currently checked children for the current parent node.
                                const checkedChildren = parent ? parent.childNodes.filter(child => child.isSelected).length : 0;

                                // If the parent's max selectable children is attained or all of the parent's children are checked, then we check the parent
                                // and all of its children and disable the children.
                                if (((parent.maxSelectableChildren && checkedChildren >= parent.maxSelectableChildren) || ((checkedChildren) === parent.childNodes.length))) {
                                    parent.isSelected = true;
                                    checkUncheckChildren(parent);
                                } else {
                                    // If not, we simply un/check any children.
                                    checkUncheckChildren(node);
                                }

                                // Update the parent and continue bubbling up the tree.
                                parent = parent.parent;

                            }
        
                            props.setData(Object.assign(props.data, {}));
                            props.setDraw(props.draw + 1);

                            if (props.onSelect) {
                                props.onSelect(node);
                            }

                        }}
                    />
                </div>
                {!hideChildren[node.value] && <div className="children">
                    {node.childNodes && node.childNodes.map((child: TreeSelectNodeData) => {
                        return renderNode(child);
                    })}
                </div>}
            </div>
        );

    }

    return (
        renderNode(props.node)
    );

}

export default TreeSelect;